import React, { useEffect } from 'react';
import { Bell, Users, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import { ClassRequestWithStudent } from '@/types/classRequests';
import { RequestCard } from './RequestCard';
import { useExpiredRequestsCleanup } from '@/shared/hooks/useExpiredRequestsCleanup';
import { getCurrentDateString } from '@/utils/timezoneUtils';

interface RequestManagementSectionProps {
  instructorId: string;
  instructorUsername: string;
  requests: ClassRequestWithStudent[];
  pendingCount: number;
  isLoading?: boolean;
  onApprove: (requestId: string) => Promise<void>;
  onDeny: (requestId: string) => Promise<void>;
  onViewProfile: (studentId: string) => void;
}

export const RequestManagementSection: React.FC<RequestManagementSectionProps> = ({
  instructorId,
  instructorUsername,
  requests,
  pendingCount,
  isLoading = false,
  onApprove,
  onDeny,
  onViewProfile
}) => {
  const { t, currentLanguage } = useI18n();
  const navigate = useNavigate();
  const cleanupMutation = useExpiredRequestsCleanup();

  // Clean up expired requests when component mounts
  useEffect(() => {
    // Only run cleanup if we have requests and not loading
    if (!isLoading && requests.length > 0) {
      cleanupMutation.mutate();
    }
  }, [isLoading, requests.length]);

  const handleSeeAll = () => {
    navigate(`/instructor-slots/${instructorUsername}`);
  };

  const handleSetupSchedule = () => {
    navigate('/calendar');
  };

  const handleSlotClick = (slotGroup: { date: Date; startTime: string; endTime: string; requests: ClassRequestWithStudent[] }) => {
    // Navigate to instructor slots page with the specific date selected
    const dateStr = slotGroup.date.toISOString().split('T')[0];
    navigate(`/instructor-slots/${instructorUsername}?date=${dateStr}`);
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="bg-[#2D2424] rounded-lg p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-[#3D3434] rounded w-1/3 mb-2"></div>
            <div className="h-3 bg-[#3D3434] rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // Filter out past requests and group by date and time slot
  // TIMEZONE HANDLING:
  // requested_date is stored as DATE (no timezone) in format 'YYYY-MM-DD'
  // We compare it with the current date in the VIEWER's local timezone.
  // This ensures correct behavior for users in different timezones:
  // - Each user sees requests based on their own local date/time
  // - A request for "2025-11-25" appears to all users on their local "2025-11-25"
  // - Time comparisons use the viewer's local time, not UTC
  const now = new Date();
  const currentDate = getCurrentDateString(); // Current date in viewer's local timezone
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`; // Current time in viewer's local timezone
  
  const filteredRequests = requests.filter(request => {
    // Only show requests that haven't passed yet (based on viewer's local date/time)
    if (request.requested_date < currentDate) {
      return false; // Past date in viewer's timezone
    }
    
    // If it's today (in viewer's timezone), check if the time has passed
    if (request.requested_date === currentDate) {
      // Normalize time format (remove seconds if present) to ensure proper comparison
      const requestTime = request.requested_start_time.split(':').slice(0, 2).join(':');
      return requestTime > currentTime; // Compare in viewer's local time
    }
    
    return true; // Future date in viewer's timezone
  });

  const groupedRequests = filteredRequests.reduce((acc, request) => {
    const key = `${request.requested_date}-${request.requested_start_time}`;
    
    if (!acc[key]) {
      // Create date in local timezone to avoid UTC conversion issues
      const [year, month, day] = request.requested_date.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      acc[key] = {
        date,
        startTime: request.requested_start_time,
        endTime: request.requested_end_time,
        requests: []
      };
    }
    acc[key].requests.push(request);
    return acc;
  }, {} as Record<string, { date: Date; startTime: string; endTime: string; requests: ClassRequestWithStudent[] }>);

  const slotGroups = Object.values(groupedRequests);
  // Sort by date and time to show earliest first
  const sortedSlots = slotGroups.sort((a, b) => {
    // First sort by date
    if (a.date.getTime() !== b.date.getTime()) {
      return a.date.getTime() - b.date.getTime();
    }
    // Then sort by start time
    return a.startTime.localeCompare(b.startTime);
  });
  
  // Calculate actual pending count from filtered requests
  const actualPendingCount = filteredRequests.filter(request => request.status === 'pending').length;
  const displaySlots = sortedSlots.slice(0, 3);

  // Empty state - no requests
  if (actualPendingCount === 0) {
    return (
      <div className="mb-8">
        <div className="bg-[#2D2424] rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-[#3D3434] rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-white font-semibold mb-2">
            {t('request_management.no_requests')}
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            {t('request_management.no_requests_subtitle')}
          </p>
          <Button
            onClick={handleSetupSchedule}
            className="bg-sossiety-red hover:bg-sossiety-red-hover text-white font-semibold"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t('request_management.setup_schedule')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white font-roboto">
          {t('request_for_classes.title')}
          {actualPendingCount > 0 && (
            <span className="text-xs font-normal text-gray-400 ml-2">
              ({actualPendingCount} {actualPendingCount === 1 ? 'request' : 'requests'})
            </span>
          )}
        </h2>
        <button
          onClick={handleSeeAll}
          className="text-xs text-white underline hover:text-gray-300 transition-colors"
        >
          {t('request_for_classes.see_all')}
        </button>
      </div>

      {/* Horizontal Scrollable Cards */}
      <div className="relative max-w-full mx-auto">
        <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex space-x-4">
            {displaySlots.map((slotGroup, index) => {
              // Format time based on language and without :00
              const formatTime = (time: string) => {
                const [hours, minutes] = time.split(':');
                const hour = parseInt(hours);
                const min = parseInt(minutes);
                
                // For Portuguese, use 24h format
                if (currentLanguage === 'pt') {
                  return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
                }
                
                // For English, use 12h format
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
              };

              return (
                <div
                  key={`${slotGroup.date.toISOString()}-${slotGroup.startTime}-${index}`}
                  className="flex-shrink-0 w-[225px] bg-[rgb(45,36,36)] rounded-lg p-4 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                >
                  <div className="mb-3">
                    <div className="text-sm font-bold text-white mb-2 font-roboto">
                      {slotGroup.date.toLocaleDateString('en-US', { 
                        day: '2-digit', 
                        weekday: 'short' 
                      })} - {formatTime(slotGroup.startTime)} to {formatTime(slotGroup.endTime)}
                    </div>
                    <div className="flex justify-start">
                      <button 
                        onClick={() => handleSlotClick(slotGroup)}
                        className="bg-sossiety-red text-white text-xs font-bold px-3 py-1 rounded-full hover:bg-sossiety-red-hover transition-colors"
                      >
                        {slotGroup.requests.length} {slotGroup.requests.length === 1 ? 'request' : 'requests'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
